import { useEffect, useRef, useState } from "react";

import { riderApi } from "../services/riderApi";
import { distanceMeters } from "../../features/delivery/maps/routeGuidance";

/**
 * Step-by-step directions for the leg a rider is currently riding.
 *
 * WHEN THIS ASKS GOOGLE, AND WHY IT MATTERS
 *
 * Every request costs money, and a rider's position updates every few seconds.
 * Asking on each fix would be both wasteful and pointless - the instructions do
 * not change because somebody moved twelve metres. So a route is fetched once
 * per leg and then re-fetched only when the rider has genuinely left it:
 *
 *   - they have moved more than REFRESH_AFTER_M from where the route was built
 *   - or the destination changed, which means a new leg entirely
 *
 * Between those, the current step is worked out locally from the position that
 * is already being tracked. No network, no cost.
 *
 * Guidance is advisory throughout. A failure is returned, never thrown, and
 * never blocks the commands that actually move the delivery forward.
 */

const REFRESH_AFTER_M = 400;
const MIN_REQUEST_GAP_MS = 20000;

function stepFor(steps, position, origin) {
  if (!steps?.length || !position) return { current: steps?.[0] || null, remainingM: null };
  // Distance travelled from where the route was built tells us how far through
  // the step list the rider is, without needing to match the road geometry.
  const travelled = origin ? distanceMeters(origin, position) : 0;
  let covered = 0;
  for (const step of steps) {
    if (covered + step.distanceM > travelled) {
      return { current: step, remainingM: Math.max(0, Math.round(covered + step.distanceM - travelled)) };
    }
    covered += step.distanceM;
  }
  return { current: steps[steps.length - 1], remainingM: 0 };
}

export default function useStepGuidance({ businessId, runId, currentPosition, destination, enabled = true }) {
  const [state, setState] = useState({ steps: [], origin: null, status: "IDLE", error: null });
  const lastRequest = useRef(0);
  const requestedFrom = useRef(null);
  const destinationKey = destination
    ? `${Number(destination.latitude).toFixed(5)},${Number(destination.longitude).toFixed(5)}`
    : null;
  // Depend on where the rider is, not on the identity of the object saying so.
  // Tracking hands down a fresh object on every fix, so depending on the object
  // re-ran this effect constantly - and its cleanup aborted the request that
  // was still in flight, so directions could never finish loading at all.
  const positionKey = currentPosition
    ? `${Number(currentPosition.latitude).toFixed(4)},${Number(currentPosition.longitude).toFixed(4)}`
    : null;
  const positionRef = useRef(currentPosition);
  positionRef.current = currentPosition;

  useEffect(() => {
    // A new leg invalidates everything learned about the old one - including the
    // throttle. That gap exists to absorb GPS jitter within a leg; making a
    // rider who has just arrived at pickup wait to find out where the first
    // customer is would be the throttle working against its own purpose.
    setState({ steps: [], origin: null, status: "IDLE", error: null });
    requestedFrom.current = null;
    lastRequest.current = 0;
  }, [destinationKey, runId]);

  useEffect(() => {
    const position = positionRef.current;
    if (!enabled || !businessId || !runId || !destinationKey || !position) return undefined;

    const movedFar = !requestedFrom.current
      || distanceMeters(requestedFrom.current, position) > REFRESH_AFTER_M;
    if (!movedFar) return undefined;
    if (Date.now() - lastRequest.current < MIN_REQUEST_GAP_MS) return undefined;

    const controller = new AbortController();
    const from = { latitude: position.latitude, longitude: position.longitude };
    lastRequest.current = Date.now();
    requestedFrom.current = from;
    setState((previous) => ({ ...previous, status: previous.steps.length ? "REFRESHING" : "LOADING" }));

    riderApi.getRunGuidance(businessId, runId, from, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ steps: data?.steps || [], origin: from, status: "READY", error: null });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        // Directions are a convenience. Losing them must not look like a fault
        // in the trip, so the previous steps are kept if there were any.
        requestedFrom.current = null;
        setState((previous) => ({
          ...previous,
          status: previous.steps.length ? "READY" : "UNAVAILABLE",
          error: error?.code || "GUIDANCE_UNAVAILABLE",
        }));
      });

    return () => controller.abort();
  }, [enabled, businessId, runId, destinationKey, positionKey]);

  const { current, remainingM } = stepFor(state.steps, currentPosition, state.origin);
  return {
    status: state.status,
    steps: state.steps,
    currentStep: current,
    remainingM,
    unavailable: state.status === "UNAVAILABLE",
  };
}
